<!-- src/components/PreviewPage.vue -->
<template>
  <div class="preview-page-container">
    <!-- 悬浮按钮容器 -->
    <div class="floating-buttons-container">
      <!-- 悬浮返回按钮 -->
      <div class="floating-btn back-btn" @click="goBack">
        <i class="el-icon-arrow-left"></i>
      </div>

      <!-- 悬浮目录按钮 -->
      <div 
        v-if="renderedContent.toc" 
        class="floating-btn toc-btn"
        @click="toggleTocSidebar"
      >
        <i class="el-icon-notebook-2"></i>
      </div>
      
      <!-- 悬浮主题选择按钮 -->
      <div class="floating-btn theme-btn">
        <i class="el-icon-postcard"></i>
        <el-select 
        v-model="previewTheme" 
        size="mini" 
        placeholder=""
        class="theme-select-dropdown"
        >
          <el-option
            v-for="theme in previewThemes"
            :key="theme.value"
            :label="theme.label"
            :value="theme.value"
          >
          </el-option>
        </el-select>
      </div>



    </div>
    
    <!-- 目录侧边栏 -->
    <transition name="slide">
      <div 
        v-show="showTocSidebar" 
        class="toc-sidebar"
      >
        <div class="toc-sidebar-header">
          <h3>目录</h3>
          <el-button 
            type="text" 
            icon="el-icon-close" 
            @click="toggleTocSidebar"
            class="toc-close-btn"
          ></el-button>
        </div>
        <div class="toc-sidebar-content" v-html="renderedContent.toc"></div>
      </div>
    </transition>
    
    <div class="preview-wrapper">

      <div class="preview-body">
        <div 
          ref="previewContent"
          id="preview-content" 
          :class="previewThemeClass"
          v-html="renderedContent.html"
          @click="handlePreviewClick"
        ></div>  
      </div>
    </div>
  </div>
</template>

<script>
import '@/css/PreviewTheme.css'; // 主题样式
import {highlightAllCode, setImgStyles} from '@/js/highlight.js';
export default {
  name: 'PreviewPage',
  components: {
  },
  data() {
    return {
      isExporting: false,
      editorContent: '',
      isSyncingScroll: false,
      previewTheme: '0',          // 预览主题，默认为空（默认主题）
      previewThemes: [            // 预览主题选项
        { value: '0', label: '默认主题' },
        { value: '7', label: '缤纷虹' },
        { value: '6', label: '可爱粉' },
        { value: '5', label: '清新蓝' },
        { value: '1', label: '经典白' },
        { value: '2', label: '暗夜黑' },
        { value: '3', label: '护眼绿' },
        { value: '4', label: '简约灰' },
      ],
      // 新增：锚点滚动相关数据
      scrollOffset: 60, // 滚动偏移量
      // 新增：目录侧边栏相关数据
      showTocSidebar: false,
      tocSidebarWidth: 250
    };
  },
  computed: {
    renderedContent(){
        return this.$store.state.renderedContent;
    },
    // 计算预览主题的CSS类名
    previewThemeClass() {
      return `theme-${this.previewTheme}`;
    }
  },
  watch: {
    showTocSidebar(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.setupTocClickHandlers();
        });
      }
    }
  },
  mounted() {
    this.$nextTick(()=>{ // 执行代码高亮和图片处理
        setImgStyles();
        highlightAllCode();
    })
  },
  beforeDestroy() {
    // 移除目录点击事件监听器
    const tocContainer = document.querySelector('.toc-sidebar-content');
    if (tocContainer) {
      tocContainer.removeEventListener('click', this.handleTocClick);
    }
  },
  methods: {
    // 返回上一页
    goBack() {
      this.$router.push("/Editor");
    },

    // 处理预览区域点击事件
    handlePreviewClick(event) {
      const link = event.target.closest('a[href^="#"]');
      if (link) {
        event.preventDefault();
        const targetId = link.getAttribute('href').substring(1);
        this.scrollToHeading(targetId);
      }
    },

    // 新增：滚动到指定标题（优化版）
    scrollToHeading(targetId) {
      try {
        const previewContainer = this.$refs.previewContent;
        if (!previewContainer || !targetId) {
          return;
        }
        
        const targetElement = previewContainer.querySelector(`#${targetId}`);
        if (!targetElement) {
          return;
        }
        
        // 使用更安全的计算方式
        const targetPosition = targetElement.offsetTop - this.scrollOffset;
        
        // 边界检查
        const maxScroll = Math.max(0, previewContainer.scrollHeight - previewContainer.clientHeight);
        const safePosition = Math.max(0, Math.min(targetPosition, maxScroll));
        
        // 验证数值有效性
        if (!isFinite(safePosition)) {
          return;
        }
        
        // 执行滚动
        previewContainer.scrollTo({
          top: safePosition,
          behavior: 'auto'
        });
        
        return;
        
      } catch (error) {
        console.error('滚动失败:', error);
        return;
      }
    },
    
    // 新增：切换目录侧边栏
    toggleTocSidebar() {
      this.showTocSidebar = !this.showTocSidebar;
    },
    
    // 新增：为目录链接添加点击事件处理
    setupTocClickHandlers() {
      const tocContainer = document.querySelector('.toc-sidebar-content');
      if (tocContainer) {
        tocContainer.removeEventListener('click', this.handleTocClick);
        tocContainer.addEventListener('click', this.handleTocClick);
      }
    },
    
    // 新增：处理目录链接点击
    handleTocClick(event) {
      const link = event.target.closest('a[href^="#"]');
      if (link) {
        event.preventDefault();
        const targetId = link.getAttribute('href').substring(1);
        if (targetId) {
          this.scrollToHeading(targetId);
          this.showTocSidebar = false;
        }
      }
    }
  },
};
</script>

<style scoped>
/* src/css/FullScreenPreview.css */

/* 页面容器样式 */
.preview-page-container {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e7f1 100%);
  display: flex;
  justify-content: center;
  align-items: center;
  /* padding: 20px; */
  box-sizing: border-box;
  position: relative;
}

/* 悬浮按钮容器样式 */
.floating-buttons-container {
  position: absolute;
  top: 5vh;
  left: 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px; /* 按钮之间的间距 */
  z-index: 100;
}

/* 通用悬浮按钮样式 */
.floating-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid rgba(255, 255, 255, 0.3);
  position: relative;
}

/* 返回按钮样式 */
.back-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.back-btn:hover {
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
  transform: scale(1.1) rotate(-15deg);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

/* 目录按钮样式 */
.toc-btn {
  background: linear-gradient(135deg, #9c27b0, #9575cd);
}

.toc-btn:hover {
  background: linear-gradient(135deg, #7b1fa2, #7e57c2);
  transform: scale(1.1) rotate(15deg);
  box-shadow: 0 6px 20px rgba(156, 39, 176, 0.4);
}

/* 主题按钮样式 */
.theme-btn {
  background: linear-gradient(135deg, #8e44ad, #9b59b6);
}

.theme-btn:hover {
  background: linear-gradient(135deg, #9b59b6, #8e44ad);
  transform: scale(1.1) rotate(15deg);
  box-shadow: 0 6px 20px rgba(142, 68, 173, 0.4);
}

/* 主题选择下拉框样式 */
.theme-select-dropdown {
  position: absolute;
  width: 100%;
  height: 100%;
  border: none;
  background: transparent;
  outline: none;
  box-shadow: none;
  opacity: 0;
  z-index: -1;
}

/* Element UI 下拉框样式覆盖 */
.theme-select-dropdown >>> .el-input__inner {
  border: none !important;
  background: transparent !important;
  color: transparent !important;
  text-align: center;
  font-size: 12px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  box-sizing: border-box;
}

.theme-select-dropdown >>> .el-input__suffix {
  display: none;
}

.theme-select-dropdown >>> .el-input__suffix-inner {
  display: none;
}

/* 图标样式 */
.floating-btn i {
  color: white;
  font-size: 18px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  z-index: 2;
  pointer-events: none;
}

.preview-wrapper {
  width: 70%; /* 默认70%宽度 */

  height: 99%;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

/* 当目录侧边栏显示时，预览区域向右移动并缩小 */
.preview-wrapper.with-toc {
  width: 85%;
  margin-left: 15%;
}

/* 目录侧边栏样式 */
.toc-sidebar {
  position: absolute;
  top: 0;
  left: 0;
  height: 97.5vh;
  width: 15vw;
  background: linear-gradient(135deg, #f3e5f5, #ffffff);
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  border-right: 1px solid rgba(156, 39, 176, 0.1);
  border-radius: 12px 0 0 12px;
}

.toc-sidebar-header {
  padding: 20px;
  border-bottom: 1px solid rgba(156, 39, 176, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(156, 39, 176, 0.05);
  border-radius: 12px 0 0 0;
}

.toc-sidebar-header h3 {
  margin: 0;
  font-size: 18px;
  color: #7b1fa2;
  font-weight: 600;
}

.toc-close-btn {
  font-size: 20px;
  padding: 0;
  color: #9c27b0;
  transition: all 0.2s ease;
}

.toc-close-btn:hover {
  color: #7b1fa2;
  transform: rotate(90deg);
}

.toc-sidebar-content {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 0 0 0 12px;
}

/* 目录内容样式 */
.toc-sidebar-content ul {
  list-style: none;
  padding-left: 0;
  margin: 0;
}

.toc-sidebar-content li {
  margin: 8px 0;
  position: relative;
}

.toc-sidebar-content a {
  text-decoration: none;
  color: #5e35b1;
  display: block;
  padding: 8px 12px;
  border-radius: 6px;
  transition: all 0.3s ease;
  font-size: 14px;
  border: 1px solid transparent;
}

.toc-sidebar-content a:hover {
  background: linear-gradient(135deg, rgba(156, 39, 176, 0.1), rgba(123, 31, 162, 0.1));
  color: #7b1fa2;
  border-color: rgba(156, 39, 176, 0.3);
  transform: translateX(4px);
}

.toc-sidebar-content ul ul {
  padding-left: 24px;
}

.toc-sidebar-content ul ul ul {
  padding-left: 24px;
}

.toc-sidebar-content ul ul a {
  color: #7e57c2;
  font-size: 13px;
}

.toc-sidebar-content ul ul ul a {
  color: #9575cd;
  font-size: 12px;
}

/* 预览主体样式 */
.preview-body {
  flex: 1;
  position: relative;
  overflow: hidden;
  padding: 10px 5px 10px 5px;
}

/* 占位符样式 */
.preview-placeholder {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #94a3b8;
  text-align: center;
  padding: 20px;
  box-sizing: border-box;
  background: #f8fafc;
  border-radius: 0 0 12px 12px;
}

.preview-placeholder-icon {
  font-size: 3em;
  margin-bottom: 20px;
  opacity: 0.7;
}

/* 动画效果 */
.slide-enter-active, .slide-leave-active {
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.slide-enter, .slide-leave-to {
  transform: translateX(-100%);
}

/* 滚动条样式 */
.toc-sidebar-content::-webkit-scrollbar {
  width: 8px;
}

.toc-sidebar-content::-webkit-scrollbar-track {
  background: #f3e5f5;
  border-radius: 4px;
}

.toc-sidebar-content::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #9c27b0, #7e57c2);
  border-radius: 4px;
}

.toc-sidebar-content::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #7b1fa2, #9575cd);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .preview-page-container {
    padding: 10px;
  }
  
  .preview-wrapper {
    width: 100%;
    height: 95%;
  }
  
  .preview-wrapper.with-toc {
    width: 85%;
    margin-left: 15%;
  }
  
  .preview-header {
    padding: 10px 15px;
    min-height: 50px;
  }
  
  .toc-sidebar {
    height: 95%;
  }
  
  .floating-buttons-container {
    top: 20px;
    left: 20px;
  }
}
</style>